"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from "react"
import { NavLink, useMatch } from "react-router-dom"
import { isUserInMapAppsAdmins, useAuth } from "@/hooks/useAuth"
import MapLogoHorizontal from "@/assets/MAP_logo_horizontal.svg?react"
import MapLogoVertical from "@/assets/MAP_logo_vertical.svg?react"
import {
  Sheet,
  BookOpen,
  Files,
  TestTubeDiagonal,
  Microscope,
  PillBottle,
  TimerReset,
  Map,
  Frame,
  PieChart,
  Ellipsis,
  Computer,
  Cpu,
  ChevronRight,
  Database,
  LayoutDashboard,
  ShieldPlus,
  MapPinHouse,
  Telescope,
  Variable,
  Feather,
  Layers,
  PenTool,
  ClipboardList,
} from "lucide-react"

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"


interface NavItemProps {
  to: string
  icon?: React.ReactNode
  children: React.ReactNode
  disabled?: boolean
}

/**
 * Returns event handlers for nav link anchors that:
 * - Close the mobile sidebar sheet on click.
 * - Prevent the sidebar container from scrolling to the focused element
 *   by saving scrollTop on mousedown and restoring it on focus.
 *
 * @returns Object with onClick, onMouseDown and onFocus handlers.
 */
function useNavLinkHandlers(): {
  onClick: () => void
  onMouseDown: (e: React.MouseEvent<HTMLAnchorElement>) => void
  onFocus: (e: React.FocusEvent<HTMLAnchorElement>) => void
} {
  const { isMobile, setOpenMobile } = useSidebar()
  const savedScroll = React.useRef<number | null>(null)

  const onClick = (): void => {
    if (isMobile) setOpenMobile(false)
  }

  const onMouseDown = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    const container = e.currentTarget.closest("[data-sidebar='content']") as HTMLElement | null
    if (container) savedScroll.current = container.scrollTop
  }

  const onFocus = (e: React.FocusEvent<HTMLAnchorElement>): void => {
    const container = e.currentTarget.closest("[data-sidebar='content']") as HTMLElement | null
    if (container && savedScroll.current !== null) {
      container.scrollTop = savedScroll.current
      savedScroll.current = null
    }
  }

  return { onClick, onMouseDown, onFocus }
}

function NavItem({ to, icon, children, disabled }: NavItemProps): React.JSX.Element {
  const match = useMatch(to)
  const isActive = match !== null
  const handlers = useNavLinkHandlers()

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive}>
        <NavLink to={to} aria-disabled={disabled ?? undefined} className="nav-item" {...handlers}>
          {icon}
          {children}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

function NavSubItem({ to, children, disabled }: NavItemProps): React.JSX.Element {
  const match = useMatch(to)
  const isActive = match !== null
  const handlers = useNavLinkHandlers()

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton asChild isActive={isActive}>
        <NavLink to={to} aria-disabled={disabled ?? undefined} className="nav-item" {...handlers}>
          {children}
        </NavLink>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  )
}

export function NavSidebar() {
  const { state } = useSidebar()
  const isExpanded = state === "expanded"
  // Diagrams is restricted to map-apps-admins (Requirement 1.1), gated the same
  // way the /diagrams routes are in routes/index.tsx.
  const { user } = useAuth()
  const userIsAdmin = isUserInMapAppsAdmins(user)

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="bg-white p-0">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton className="min-h-[48px] min-w-[48px] border-b">
              {!isExpanded && (
                <MapLogoVertical className="!w-[32px] !h-[32px] shrink-0 m-auto" aria-label="MAP" />
              )}
              {isExpanded && (
                <MapLogoHorizontal
                  className="!w-[160px] !h-[32px] m-auto"
                  aria-label="Malaria Atlas Project"
                />
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* AI Assistants */}
        <SidebarGroup>
          <SidebarGroupLabel>
            AI Assistants
          </SidebarGroupLabel>
          <SidebarMenu>
            <NavItem to="/ai/exploratory-literature" icon={<Telescope className="size-4 mr-1" />}>
              <span>Exploratory Literature Review</span>
            </NavItem>
            <NavItem to="/ai/deep-literature" icon={<Feather className="size-4 mr-1" />}>
              <span>Deep Literature Review</span>
            </NavItem>
            <NavItem to="/ai/survey-variable" icon={<Variable className="size-4 mr-1" />}>
              <span>Survey Variable Discovery</span>
            </NavItem>
          </SidebarMenu>
        </SidebarGroup>

        {/* Data Sources */}
        <SidebarGroup>
          <SidebarGroupLabel>Data Sources</SidebarGroupLabel>
          <SidebarMenu>
            <NavItem to="/all-sources" icon={<Layers className="size-4" />}>
              <span>All Sources</span>
            </NavItem>
            <NavItem to="/survey-catalogue" icon={<Sheet className="size-4" />}>
              <span>Survey Catalogue</span>
            </NavItem>
            <NavItem to="/articles" icon={<BookOpen className="size-4" />}>
              <span>Journal Articles</span>
            </NavItem>
            <NavItem to="/datasets" icon={<Database className="size-4" />}>
              <span>Datasets</span>
            </NavItem>
            <NavItem to="/data-portals" icon={<LayoutDashboard className="size-4" />} disabled>
              <span>Data Portals</span>
            </NavItem>
            <NavItem to="/reports" icon={<Files className="size-4" />}>
              <span>Reports</span>
            </NavItem>
          </SidebarMenu>
        </SidebarGroup>

        {/* Data Consolidation */}
        <SidebarGroup>
          <SidebarGroupLabel>Data Consolidation</SidebarGroupLabel>
          <SidebarMenu>
            <NavItem to="/pr-literature-review" icon={<ClipboardList className="size-4" />}>
              <span>PR Literature Review</span>
            </NavItem>
          </SidebarMenu>
        </SidebarGroup>

        {/* Data */}
        <SidebarGroup>
          <SidebarGroupLabel>Data</SidebarGroupLabel>
          <SidebarMenu>
            <Collapsible defaultOpen className="group/collapsible">
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton>
                    <TestTubeDiagonal className="size-4" />
                    <span>Parasite Rate</span>
                    <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    <NavSubItem to="/data/parasite-rate/clusters">
                      <span>PR Clusters</span>
                    </NavSubItem>
                    <NavSubItem to="/data/parasite-rate/polygons">
                      <span>PR Polygons</span>
                    </NavSubItem>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>

            <Collapsible defaultOpen className="group/collapsible">
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton>
                    <Microscope className="size-4" />
                    <span>Routine Surveillance</span>
                    <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    <NavSubItem to="/data/rs/hf">
                      <span>Health Facilities</span>
                    </NavSubItem>
                    <NavSubItem to="/data/rs/subnational">
                      <span>Subnational</span>
                    </NavSubItem>
                    {import.meta.env.VITE_ENVIRONMENT === "dev" && (
                      <NavSubItem to="/data/rs/national">
                        <span>National</span>
                      </NavSubItem>
                    )}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>

            <Collapsible defaultOpen className="group/collapsible">
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton>
                    <ShieldPlus className="size-4" />
                    <span>Interventions</span>
                    <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    <NavSubItem to="/data/interventions/itn">
                      <span>ITN</span>
                    </NavSubItem>
                    <NavSubItem to="/data/interventions/irs">
                      <span>IRS</span>
                    </NavSubItem>
                    <NavSubItem to="/data/interventions/smc">
                      <span>SMC</span>
                    </NavSubItem>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>

            <Collapsible defaultOpen className="group/collapsible">
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton>
                    <PillBottle className="size-4" />
                    <span>Case Management</span>
                    <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    <NavSubItem to="/data/case-management/fts">
                      <span>Fever Treatment</span>
                    </NavSubItem>
                    <NavSubItem to="/data/case-management/am">
                      <span>Antimalarials</span>
                    </NavSubItem>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>

            <NavItem to="/data/seasonality" icon={<TimerReset className="size-4" />}>
              <span>Seasonality</span>
            </NavItem>
            <NavItem to="/data/housing" icon={<MapPinHouse className="size-4" />}>
              <span>Housing</span>
            </NavItem>
            <NavItem to="/data/covariates" icon={<Map className="size-4" />}>
              <span>Covariates</span>
            </NavItem>
          </SidebarMenu>
        </SidebarGroup>

        {/* Modelling */}
        <SidebarGroup>
          <SidebarGroupLabel>Modelling</SidebarGroupLabel>
          <SidebarMenu>
            <NavItem to="/modelling/pf" icon={<Frame className="size-4" />}>
              <span className="italic">Plasmodium falciparum</span>
            </NavItem>
            <NavItem to="/modelling/global-burden" icon={<PieChart className="size-4" />} disabled>
              <span>Global Burden</span>
            </NavItem>
            <NavItem to="/modelling/more" icon={<Ellipsis className="size-4" />} disabled>
              <span>More</span>
            </NavItem>
          </SidebarMenu>
        </SidebarGroup>

        {/* Computing */}
        <SidebarGroup>
          <SidebarGroupLabel>Computing</SidebarGroupLabel>
          <SidebarMenu>
            <NavItem to="/computing/posit" icon={<Computer className="size-4" />}>
              <span>Posit Workbench</span>
            </NavItem>
            <NavItem to="/computing/hpc" icon={<Cpu className="size-4" />}>
              <span>HPC Jobs</span>
            </NavItem>
            <Collapsible defaultOpen className="group/collapsible">
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton>
                    <PieChart className="size-4" />
                    <span>Resource Usage</span>
                    <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    <NavSubItem to="/computing/resource-usage/disk">
                      <span>Disk Usage</span>
                    </NavSubItem>
                    <NavSubItem to="/computing/resource-usage/token">
                      <span>Token Usage</span>
                    </NavSubItem>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          </SidebarMenu>
        </SidebarGroup>

        {/* Other Tools — shown only when this user can see a tool in it, so
            non-admins get no empty heading. Its only tool, Diagrams, is
            admin-only (Requirement 1.1); when a tool for everyone is added,
            gate Diagrams on its own and show the section whenever any of its
            items is visible. */}
        {userIsAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel>Other Tools</SidebarGroupLabel>
            <SidebarMenu>
              <NavItem to="/diagrams" icon={<PenTool className="size-4" />}>
                <span>Diagrams</span>
              </NavItem>
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarRail />
    </Sidebar>
  )
}
